import { Link } from '@tanstack/react-router';
import { Building2, Check, CheckSquare, ChevronsUpDown, Plus, Settings, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Menu, MenuLabel, MenuSeparator } from '../../components/Menu';
import { menuItemStyles } from '../../components/styles';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useAuth } from '../../stores/auth';

/**
 * La cabecera de la barra de empresa: dice en qué empresa estás y es la única
 * salida de ella.
 *
 * Concentra los dos gestos que antes estaban repartidos por toda la barra
 * —saltar a otra empresa y salir a lo global— para que las veintiuna secciones
 * de debajo puedan ser lo único que ocupa la barra. Es también el motivo de que
 * lo global viva aquí dentro y no en un bloque al pie: un bloque al pie compite
 * por la atención con la navegación de la empresa, y quien entra en una empresa
 * viene a trabajar en ella.
 */
export function CompanySwitcher({
  companySlug,
  companyName,
  onNavigate,
}: {
  companySlug: string;
  companyName: string;
  onNavigate: () => void;
}) {
  const t = useT();
  const companies = useAuth((state) => state.user?.companies ?? []);

  const globals: { to: string; label: Key; icon: LucideIcon }[] = [
    { to: '/', label: 'nav.companies', icon: Building2 },
    { to: '/approvals', label: 'nav.approvals', icon: CheckSquare },
    { to: '/notifications', label: 'nav.notifications', icon: Sparkles },
    { to: '/settings', label: 'nav.account', icon: Settings },
  ];

  return (
    <Menu
      align="start"
      className="w-[calc(var(--spacing-sidebar)-2rem)]"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={t('nav.switchCompany')}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-sm px-2 py-2 text-left transition-colors duration-fast',
            open ? 'bg-white/10' : 'hover:bg-white/6',
          )}
        >
          {/*
            La inicial es decorativa: el nombre va escrito justo al lado. Sin
            `aria-hidden` se cuela en el nombre accesible y el control pasa a
            llamarse «A Acme Robotics».
          */}
          <span
            aria-hidden="true"
            className="brand-gradient grid size-8 shrink-0 place-items-center rounded-xs text-[0.7rem] font-extrabold text-white"
          >
            {companyName.charAt(0).toUpperCase()}
          </span>
          <span className="min-w-0 grow">
            <span className="block truncate text-sm font-semibold text-white">{companyName}</span>
            <span className="block truncate text-[0.66rem] text-nav-ink-soft">{t('nav.switchCompany')}</span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-nav-ink-soft" aria-hidden="true" />
        </button>
      )}
    >
      {({ close }) => (
        <>
          <MenuLabel>{t('nav.companies')}</MenuLabel>

          {/*
            Enlaces y no botones: son navegación, así que tienen que poder
            abrirse en otra pestaña y copiarse. `menuItemStyles` existe justo
            para esto.
          */}
          <div className="max-h-64 overflow-y-auto">
            {companies.map((company) => {
              const active = company.slug === companySlug;

              return (
                <Link
                  key={company.id}
                  to="/c/$companySlug"
                  params={{ companySlug: company.slug }}
                  onClick={() => {
                    close();
                    onNavigate();
                  }}
                  className={menuItemStyles(active)}
                >
                  <span
                    aria-hidden="true"
                    className="brand-gradient grid size-5 shrink-0 place-items-center rounded-xs text-[0.5rem] font-extrabold text-white"
                  >
                    {company.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="grow truncate">{company.name}</span>
                  {active ? <Check className="size-3.5 shrink-0" aria-hidden="true" /> : null}
                </Link>
              );
            })}
          </div>

          <Link
            to="/companies/new"
            onClick={() => {
              close();
              onNavigate();
            }}
            className={menuItemStyles()}
          >
            <span className="shrink-0 [&>svg]:size-4">
              <Plus />
            </span>
            <span className="grow truncate">{t('nav.newCompany')}</span>
          </Link>

          <MenuSeparator />

          <MenuLabel>{t('nav.global')}</MenuLabel>

          {globals.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to as '/'}
              onClick={() => {
                close();
                onNavigate();
              }}
              className={menuItemStyles()}
            >
              <span className="shrink-0 [&>svg]:size-4">
                <Icon />
              </span>
              <span className="grow truncate">{t(label)}</span>
            </Link>
          ))}
        </>
      )}
    </Menu>
  );
}
